export const OFFSETS = {
    VERSION: 0,
    COL_COUNT: 1,
    ROW_COUNT: 2,
    SCROLL_X: 3,
    SCROLL_Y: 4,
    ZOOM: 5, // stored as float
    VIEWPORT_W: 6,
    VIEWPORT_H: 7,
    // Data starts here
    HEADER_SIZE: 16, // Reserve some extra space
    // Dynamic offsets calculated at runtime based on col/row counts
    COLS_START: 16,
    // ROWS_START = COLS_START + col_count
};

const DEFAULT_COL_WIDTH = 100;
const DEFAULT_ROW_HEIGHT = 24;

type BufferLike = SharedArrayBuffer | ArrayBuffer;

export class SharedState {
    private i32: Int32Array;
    private f32: Float32Array;
    private buffer: BufferLike;
    private isShared: boolean;

    constructor(buffer: BufferLike) {
        this.buffer = buffer;
        this.isShared = typeof SharedArrayBuffer !== 'undefined' && buffer instanceof SharedArrayBuffer;
        this.i32 = new Int32Array(buffer);
        this.f32 = new Float32Array(buffer);
    }

    static create(cols: number, rows: number): BufferLike {
        // Calculate size
        // Header + ColWidths + RowHeights
        const dataSize = (cols + rows) * 4; // 4 bytes per float
        const totalSize = (OFFSETS.HEADER_SIZE * 4) + dataSize;

        let buf: BufferLike;
        try {
            buf = new SharedArrayBuffer(totalSize);
        } catch (_e) {
            console.warn('[SharedState] SharedArrayBuffer unavailable; falling back to ArrayBuffer (no Atomics)');
            buf = new ArrayBuffer(totalSize);
        }

        const i32 = new Int32Array(buf);
        const f32 = new Float32Array(buf);

        // Init Header
        // If buffer is not shared, Atomics.* will throw; guard
        const useAtomics = typeof SharedArrayBuffer !== 'undefined' && buf instanceof SharedArrayBuffer;
        if (useAtomics) {
            Atomics.store(i32, OFFSETS.VERSION, 0);
            Atomics.store(i32, OFFSETS.COL_COUNT, cols);
            Atomics.store(i32, OFFSETS.ROW_COUNT, rows);
        } else {
            i32[OFFSETS.VERSION] = 0;
            i32[OFFSETS.COL_COUNT] = cols;
            i32[OFFSETS.ROW_COUNT] = rows;
        }

        // Initialize default column widths and row heights
        for (let c = 0; c < cols; c++) {
            f32[OFFSETS.COLS_START + c] = DEFAULT_COL_WIDTH;
        }
        const rowsStart = OFFSETS.COLS_START + cols;
        for (let r = 0; r < rows; r++) {
            f32[rowsStart + r] = DEFAULT_ROW_HEIGHT;
        }

        return buf;
    }

    getBuffer(): BufferLike {
        return this.buffer;
    }

    // View State
    setScroll(x: number, y: number) {
        if (this.isShared) {
            Atomics.store(this.i32, OFFSETS.SCROLL_X, Math.round(x));
            Atomics.store(this.i32, OFFSETS.SCROLL_Y, Math.round(y));
        } else {
            this.i32[OFFSETS.SCROLL_X] = Math.round(x);
            this.i32[OFFSETS.SCROLL_Y] = Math.round(y);
        }
    }

    getScroll(): { x: number, y: number } {
        return {
            x: this.isShared ? Atomics.load(this.i32, OFFSETS.SCROLL_X) : this.i32[OFFSETS.SCROLL_X],
            y: this.isShared ? Atomics.load(this.i32, OFFSETS.SCROLL_Y) : this.i32[OFFSETS.SCROLL_Y],
        };
    }

    setZoom(zoom: number) {
        this.f32[OFFSETS.ZOOM] = zoom;
    }

    getZoom(): number {
        return this.f32[OFFSETS.ZOOM] || 1.0;
    }

    setViewport(width: number, height: number) {
        if (this.isShared) {
            Atomics.store(this.i32, OFFSETS.VIEWPORT_W, Math.round(width));
            Atomics.store(this.i32, OFFSETS.VIEWPORT_H, Math.round(height));
        } else {
            this.i32[OFFSETS.VIEWPORT_W] = Math.round(width);
            this.i32[OFFSETS.VIEWPORT_H] = Math.round(height);
        }
    }

    getViewport(): { width: number, height: number } {
        return {
            width: this.isShared ? Atomics.load(this.i32, OFFSETS.VIEWPORT_W) : this.i32[OFFSETS.VIEWPORT_W],
            height: this.isShared ? Atomics.load(this.i32, OFFSETS.VIEWPORT_H) : this.i32[OFFSETS.VIEWPORT_H],
        };
    }

    // Layout
    getColCount(): number {
        return Atomics.load(this.i32, OFFSETS.COL_COUNT);
    }

    getRowCount(): number {
        return Atomics.load(this.i32, OFFSETS.ROW_COUNT);
    }

    setColWidth(col: number, width: number) {
        const colCount = this.getColCount();
        if (col >= colCount) return;
        // Float32Array doesn't support Atomics, but layout updates are usually single-writer (Worker or Main)
        // We might need a lock if both write. For now, assume Main writes on resize, Worker writes on init.
        this.f32[OFFSETS.COLS_START + col] = width;
    }

    getColWidth(col: number): number {
        const colCount = this.getColCount();
        if (col >= colCount) return 0;
        return this.f32[OFFSETS.COLS_START + col];
    }

    setRowHeight(row: number, height: number) {
        const colCount = this.getColCount();
        const rowCount = this.getRowCount();
        if (row >= rowCount) return;

        const rowsStart = OFFSETS.COLS_START + colCount;
        this.f32[rowsStart + row] = height;
    }

    getRowHeight(row: number): number {
        const colCount = this.getColCount();
        const rowCount = this.getRowCount();
        if (row >= rowCount) return 0;

        const rowsStart = OFFSETS.COLS_START + colCount;
        return this.f32[rowsStart + row];
    }
}
